'use client';

import Image from 'next/image';
import { DEFAULT_BRAND_COLOR } from '@lib/onboarding/constants/setup-options';
import { cn } from 'cn';
import { motion } from 'motion/react';

/** Previews wear the default brand color until the user picks one. */
export const DEFAULT_ACCENT = DEFAULT_BRAND_COLOR;

/** Card style shared with the sign-up page's feature panel. */
const PREVIEW_CARD_CLASS = 'rounded-[25px] border border-gray-200 bg-white';

/** A white preview card, optionally labelled, that sits in the right panel. */
export function PreviewWindow({
  title,
  children,
  className
}: {
  title?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn('w-full p-6', PREVIEW_CARD_CLASS, className)} data-sentry-component="PreviewWindow" data-sentry-source-file="preview-frame.tsx">
      {title ? <p className="mb-4 truncate text-xs font-medium text-gray-500">
          {title}
        </p> : null}
      {children}
    </div>;
}

/** Grey placeholder text line. */
export function Line({
  width = '100%',
  className,
  delay = 0
}: {
  width?: string;
  className?: string;
  delay?: number;
}) {
  return <motion.div className={cn('h-2 rounded-full bg-gray-200', className)} style={{
    width
  }} initial={{
    scaleX: 0,
    originX: 0
  }} animate={{
    scaleX: 1
  }} transition={{
    delay,
    duration: 0.5,
    ease: 'easeOut'
  }} data-sentry-element="motion.div" data-sentry-component="Line" data-sentry-source-file="preview-frame.tsx" />;
}
export function initialsFor(name: string, fallback = 'C'): string {
  const initials = name.trim().split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  return initials || fallback;
}

/** Square workspace mark: the uploaded logo, or initials on the accent. */
export function WorkspaceMark({
  name,
  logoUrl,
  accent,
  size = 40,
  animateLayout = true
}: {
  name: string;
  logoUrl: string | null;
  accent: string;
  size?: number;
  /** Off inside parents that move, where layout animation drifts. */
  animateLayout?: boolean;
}) {
  if (logoUrl) {
    return <Image src={logoUrl} alt="" unoptimized width={size} height={size} className="shrink-0 rounded-xl border border-gray-100 bg-white object-contain" style={{
      width: size,
      height: size
    }} />;
  }
  return <motion.div layout={animateLayout} className="flex shrink-0 items-center justify-center rounded-xl font-semibold text-white" style={{
    width: size,
    height: size,
    backgroundColor: accent,
    fontSize: size * 0.38
  }} data-sentry-element="motion.div" data-sentry-component="WorkspaceMark" data-sentry-source-file="preview-frame.tsx">
      {initialsFor(name)}
    </motion.div>;
}
export function Pill({
  children,
  tone = 'gray',
  className
}: {
  children: React.ReactNode;
  tone?: 'gray' | 'green' | 'amber' | 'blue';
  className?: string;
}) {
  const tones = {
    gray: 'bg-gray-100 text-gray-600',
    green: 'bg-green-100 text-green-700',
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-sky-100 text-sky-700'
  };
  return <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium', tones[tone], className)} data-sentry-component="Pill" data-sentry-source-file="preview-frame.tsx">
      {children}
    </span>;
}